import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { cn } from "../../lib/utils";
import { Z_INDEX } from "../../lib/z-index";
import { DialogContentContext } from "./DialogContentContext";
import { DialogOverlay } from "./DialogOverlay";
import { DialogPortal } from "./DialogPortal";

export const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  disableCloseOnClickOutside = false,
  divided = false,
  overlayVariant = "dark",
  onInteractOutside,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  disableCloseOnClickOutside?: boolean;
  divided?: boolean;
  overlayVariant?: "light" | "dark";
}) => {
  // Prevent dialog from closing when clicking on elements marked as interactive with dialog
  const handleInteractOutside: ComponentProps<typeof DialogPrimitive.Content>["onInteractOutside"] = (e) => {
    const originalEvent = (e as CustomEvent).detail?.originalEvent;
    const target = (originalEvent?.target ?? e.target) as HTMLElement;
    if (disableCloseOnClickOutside || target?.closest("[data-interactive-with-dialog]")) {
      e.preventDefault();
    }
    onInteractOutside?.(e);
  };

  const isLightOverlay = overlayVariant === "light";

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={isLightOverlay ? "bg-white/80 backdrop-blur-sm" : undefined} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        style={{
          "--z": Z_INDEX.modal + 1,
        }}
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] rounded-lg border duration-200 sm:max-w-lg z-[var(--z)]",
          isLightOverlay ? "" : "shadow-lg",
          divided ? "gap-0" : "gap-4 p-6",
          className
        )}
        onInteractOutside={handleInteractOutside}
        {...props}
      >
        <DialogContentContext.Provider value={{ divided }}>{children}</DialogContentContext.Provider>
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};
